import { useId, type CSSProperties, type ReactNode } from 'react';
import { OPTION_COLORS } from '@/components/organisms/proposals/support/proposal-option-math';

/** Comparing an option never accepts it; approval remains a separate action. */
export function ProposalOptionTabs({
  options,
  activeIndex,
  onSelect,
  children,
}: {
  options: Array<{ label: string; total: number; recommended?: boolean; color?: string }>;
  activeIndex: number;
  onSelect: (index: number) => void;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <div className="min-w-0">
      {/* A phone scrolls the options sideways in one row instead of stacking them. */}
      <div
        role="tablist"
        aria-label="Proposal options"
        className="mb-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] sm:snap-none sm:flex-wrap sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden"
      >
        {options.map((option, index) => (
          <button
            key={index}
            id={`${id}-tab-${index}`}
            type="button"
            role="tab"
            aria-controls={`${id}-panel`}
            aria-selected={index === activeIndex}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => onSelect(index)}
            onKeyDown={(event) => {
              let next: number;
              switch (event.key) {
                case 'ArrowRight':
                  next = (index + 1) % options.length;
                  break;
                case 'ArrowLeft':
                  next = (index - 1 + options.length) % options.length;
                  break;
                case 'Home':
                  next = 0;
                  break;
                case 'End':
                  next = options.length - 1;
                  break;
                default:
                  return;
              }
              event.preventDefault();
              onSelect(next);
              const tabs =
                event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>(
                  '[role="tab"]',
                );
              tabs?.[next]?.focus();
            }}
            style={
              {
                '--proposal-option-color':
                  option.color ?? OPTION_COLORS[index % OPTION_COLORS.length],
                '--proposal-option-border':
                  index === activeIndex
                    ? 'var(--proposal-option-color)'
                    : 'color-mix(in srgb, var(--proposal-option-color) 25%, transparent)',
                borderLeftColor: 'var(--proposal-option-border)',
                borderRightColor: 'var(--proposal-option-border)',
                borderBottomColor: 'var(--proposal-option-border)',
                borderTopColor: 'var(--proposal-option-color)',
                backgroundColor: `color-mix(in srgb, var(--proposal-option-color) ${index === activeIndex ? 10 : 6}%, white)`,
              } as CSSProperties
            }
            className={`flex w-[42%] min-w-[140px] shrink-0 snap-start flex-col gap-1 sm:w-auto sm:min-w-[132px] sm:flex-1 sm:shrink rounded-lg border border-t-[3px] px-4 py-3 text-left text-gray-900 transition-colors hover:brightness-[0.98] ${
              index === activeIndex ? 'shadow-sm' : ''
            }`}
          >
            <span className="flex flex-wrap items-center gap-2 text-sm font-medium">
              {option.label || `Option ${index + 1}`}
              {option.recommended ? (
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700"
                  style={{
                    backgroundColor: 'color-mix(in srgb, var(--proposal-option-color) 18%, white)',
                  }}
                >
                  Recommended
                </span>
              ) : null}
            </span>
            <span className="text-lg font-semibold tabular-nums">
              {option.total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </span>
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-tab-${activeIndex}`}
        tabIndex={0}
      >
        {children}
      </div>
    </div>
  );
}
